// Sistema centralizado de banco de preguntas médicas profesionales
import { headQuestionsAdvanced } from './advanced/headQuestionsAdvanced';
import { neckQuestionsAdvanced } from './advanced/neckQuestionsAdvanced';
import { bicepsBraquialQuestions } from './muscles/bicepsBraquial';
import { tricepsBraquialQuestions } from './muscles/tricepsBraquial';
import { deltoidesQuestions } from './muscles/deltoides';
import { cuadricepsFemoralQuestions } from './muscles/cuadricepsFemoral';
import { pectoralMayorQuestions } from './muscles/pectoralMayor';
import { gluteoMayorQuestions } from './muscles/gluteoMayor';
import { trapecioQuestions } from './muscles/trapecio';
import { esternocleidomastoideoQuestions } from './muscles/esternocleidomastoideo';
import { escalenosQuestions } from './muscles/escalenos';
import { digastricoQuestions } from './muscles/digastrico';
import { milohioideoQuestions } from './muscles/milohioideo';
import { platismaQuestions } from './muscles/platisma';
import { DIFFICULTY_LEVELS, QUESTION_TYPES } from './questionTypes';
import { trueFalseQuestions } from './trueFalseQuestions';

// Importar preguntas existentes y agregar metadata
import { headQuestions } from './headQuestions';
import { neckQuestions } from './neckQuestions';
import { thoraxQuestions } from './thoraxQuestions';
import { abdomenQuestions } from './abdomenQuestions';
import { backQuestions } from './backQuestions';
import { legQuestions } from './legQuestions';
import { pelvisQuestions } from './pelvisQuestions';
import { armQuestions } from './armQuestions';
import { additionalQuestions } from './additionalQuestions';

// Función para agregar metadata a preguntas antiguas
const addMetadataToOldQuestions = (questions, defaultDifficulty = DIFFICULTY_LEVELS.BASICO) => {
  return questions.map(q => ({
    ...q,
    difficulty: q.difficulty || defaultDifficulty,
    type: q.type || QUESTION_TYPES.CONCEPTO
  }));
};

// Consolidar todas las preguntas
export const allQuestions = [
  // Preguntas avanzadas nuevas
  ...headQuestionsAdvanced,
  ...neckQuestionsAdvanced,
  
  // Preguntas por músculo (30 preguntas cada uno)
  ...bicepsBraquialQuestions,
  ...tricepsBraquialQuestions,
  ...deltoidesQuestions,
  ...cuadricepsFemoralQuestions,
  ...pectoralMayorQuestions,
  ...gluteoMayorQuestions,
  ...trapecioQuestions,
  ...esternocleidomastoideoQuestions,
  ...escalenosQuestions,
  ...digastricoQuestions,
  ...milohioideoQuestions,
  ...platismaQuestions,
  
  // Preguntas de verdadero/falso
  ...trueFalseQuestions,
  
  // Preguntas existentes con metadata
  ...addMetadataToOldQuestions(headQuestions, DIFFICULTY_LEVELS.BASICO),
  ...addMetadataToOldQuestions(neckQuestions, DIFFICULTY_LEVELS.GENERAL),
  ...addMetadataToOldQuestions(thoraxQuestions, DIFFICULTY_LEVELS.BASICO),
  ...addMetadataToOldQuestions(abdomenQuestions, DIFFICULTY_LEVELS.BASICO),
  ...addMetadataToOldQuestions(backQuestions, DIFFICULTY_LEVELS.BASICO),
  ...addMetadataToOldQuestions(legQuestions, DIFFICULTY_LEVELS.BASICO),
  ...addMetadataToOldQuestions(pelvisQuestions, DIFFICULTY_LEVELS.BASICO),
  ...addMetadataToOldQuestions(armQuestions, DIFFICULTY_LEVELS.GENERAL),
  ...addMetadataToOldQuestions(additionalQuestions, DIFFICULTY_LEVELS.GENERAL)
];

// Funciones de filtrado avanzado
export const getQuestionsByFilters = (filters = {}) => {
  const {
    regions = [],
    difficulties = [],
    types = [],
    excludeIds = []
  } = filters;

  let filtered = allQuestions;

  // Filtrar por región
  if (regions.length > 0) {
    filtered = filtered.filter(q => regions.includes(q.region));
  }

  // Filtrar por dificultad
  if (difficulties.length > 0) {
    filtered = filtered.filter(q => difficulties.includes(q.difficulty));
  }

  // Filtrar por tipo
  if (types.length > 0) {
    filtered = filtered.filter(q => types.includes(q.type));
  }

  // Excluir IDs ya preguntados
  if (excludeIds.length > 0) {
    filtered = filtered.filter(q => !excludeIds.includes(q.id));
  }

  return filtered;
};

export const getRandomQuestionAdvanced = (filters = {}) => {
  const available = getQuestionsByFilters(filters);
  
  if (available.length === 0) return null;
  
  const randomIndex = Math.floor(Math.random() * available.length);
  return available[randomIndex];
};

// Estadísticas del banco de preguntas
export const getQuestionBankStats = () => {
  const stats = {
    total: allQuestions.length,
    byRegion: {},
    byDifficulty: {},
    byType: {}
  };

  allQuestions.forEach(q => {
    // Por región
    stats.byRegion[q.region] = (stats.byRegion[q.region] || 0) + 1;
    
    // Por dificultad
    stats.byDifficulty[q.difficulty] = (stats.byDifficulty[q.difficulty] || 0) + 1;
    
    // Por tipo
    stats.byType[q.type] = (stats.byType[q.type] || 0) + 1;
  });

  return stats;
};

// Validar integridad del banco de preguntas
export const validateQuestionBank = () => {
  const errors = [];
  const ids = new Set();

  allQuestions.forEach((q, index) => {
    // Validar ID único
    if (ids.has(q.id)) {
      errors.push(`Duplicate ID: ${q.id} at index ${index}`);
    }
    ids.add(q.id);

    // Validar campos requeridos
    if (!q.question) errors.push(`Missing question at index ${index}`);
    if (!q.options || (q.options.length !== 4 && q.options.length !== 2)) {
      errors.push(`Invalid options length (must be 2 or 4) at index ${index}`);
    }
    if (
      q.correctAnswer === undefined ||
      q.correctAnswer < 0 ||
      !q.options ||
      q.correctAnswer >= q.options.length
    ) {
      errors.push(`Invalid correctAnswer at index ${index}`);
    }
    if (!q.region) errors.push(`Missing region at index ${index}`);
    if (!q.difficulty) errors.push(`Missing difficulty at index ${index}`);
    if (!q.type) errors.push(`Missing type at index ${index}`);
  });

  return {
    valid: errors.length === 0,
    errors,
    totalQuestions: allQuestions.length
  };
};

// Log de estadísticas en desarrollo
if (process.env.NODE_ENV === 'development') {
  const stats = getQuestionBankStats();
  const validation = validateQuestionBank();
  
  console.log('📊 Question Bank Statistics:');
  console.log(`Total Questions: ${stats.total}`);
  console.log('By Region:', stats.byRegion);
  console.log('By Difficulty:', stats.byDifficulty);
  console.log('By Type:', stats.byType);
  console.log('Validation:', validation.valid ? '✅ PASSED' : '❌ FAILED');
  
  if (!validation.valid) {
    console.error('Validation Errors:', validation.errors);
  }
}
